import dayjs from 'dayjs';
import {
  PerformanceReportingMetricsResponse,
  PerformanceReportingTrendsResponse,
  PerformanceReportingDimensionsResponse,
  PerformanceReportingTop5SummariesResponse,
  TrendValue,
} from '../../_types';
import {
  DISCIPLINARY_ACTION_VALUES,
  DISPOSITION_VALUES,
  INCIDENT_TYPE_VALUES,
  CASE_TYPE_VALUES,
  COMPLAINT_NATURE_VALUES,
} from './internalAffairsLookups';
import { formatTooltipDescription } from '../../_constants';

/**
 * Type representing an Internal Affairs event record
 * 
 * This matches the structure of records in ia_events.json with additional
 * fields for OrgId, DisciplinaryAction, ComplaintNature, and Disposition.
 * These records will be dynamically generated and used as base data to
 * calculate metrics.
 */
export interface InternalAffairsEvent {
  OrgId: string; // UUID of the organization
  EventDate: string; // ISO date string (YYYY-MM-DD)
  IncidentType: string | null; // Type of incident (from INCIDENT_TYPE_VALUES)
  CaseType: string | null; // Type of case (from CASE_TYPE_VALUES)
  DisciplinaryAction: string; // Disciplinary action taken (from DISCIPLINARY_ACTION_VALUES)
  ComplaintNature: string; // Nature of complaint (from COMPLAINT_NATURE_VALUES)
  Disposition: string; // Disposition of case (from DISPOSITION_VALUES)
  DateAssigned: string | null; // Date assigned (nullable)
  DateCompleted: string | null; // Date completed (nullable)
}

/**
 * Helper to select a random item from an array
 */
function randomItem<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

/**
 * Helper to generate a random date between start and end dates
 */
function randomDate(startDate: string, endDate: string): string {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const diffDays = end.diff(start, 'day');
  const randomDays = Math.floor(Math.random() * diffDays);
  return start.add(randomDays, 'day').format('YYYY-MM-DD');
}

/**
 * Helper to determine if a disposition is "sustained"
 */
function isSustained(disposition: string): boolean {
  const sustainedKeywords = ['sustained', 'SUSTAINED'];
  return sustainedKeywords.some((keyword) =>
    disposition.toLowerCase().includes(keyword.toLowerCase())
  );
}

/**
 * Helper to determine if disciplinary action was taken
 */
function hasDisciplinaryAction(disciplinaryAction: string): boolean {
  const noActionKeywords = [
    'none',
    'NONE',
    'no action',
    'No Action',
    'N/A',
    'NA',
    'Not Applicable',
    'Exonerated',
    'Unfounded',
    'Not Sustained',
    'Non-Sustained',
  ];
  return !noActionKeywords.some((keyword) =>
    disciplinaryAction.toLowerCase().includes(keyword.toLowerCase())
  );
}

/**
 * Generate a single Internal Affairs event
 */
function generateEvent(
  orgId: string,
  startDate: string,
  endDate: string
): InternalAffairsEvent {
  const eventDate = randomDate(startDate, endDate);

  // Generate IncidentType (80% chance of having a value)
  const incidentType: string | null = Math.random() < 0.8 ? randomItem(INCIDENT_TYPE_VALUES) : null;

  // Generate CaseType (75% chance of having a value)
  const caseType: string | null = Math.random() < 0.75 ? randomItem(CASE_TYPE_VALUES) : null;

  // Generate ComplaintNature
  const complaintNature = randomItem(COMPLAINT_NATURE_VALUES);

  // Generate Disposition (weighted toward common outcomes)
  // Sustained: ~30%, Not Sustained: ~25%, Exonerated: ~15%, Unfounded: ~10%, Other: ~20%
  let disposition: string;
  const dispositionRand = Math.random();
  if (dispositionRand < 0.30) {
    // Sustained
    disposition = randomItem(DISPOSITION_VALUES.filter(d => isSustained(d)));
  } else if (dispositionRand < 0.55) {
    // Not Sustained
    disposition = randomItem(DISPOSITION_VALUES.filter(d =>
      d.toLowerCase().includes('not sustained') || d.toLowerCase().includes('non-sustained')
    ));
  } else if (dispositionRand < 0.70) {
    // Exonerated
    disposition = randomItem(DISPOSITION_VALUES.filter(d =>
      d.toLowerCase().includes('exonerated')
    ));
  } else if (dispositionRand < 0.80) {
    // Unfounded
    disposition = randomItem(DISPOSITION_VALUES.filter(d =>
      d.toLowerCase().includes('unfounded')
    ));
  } else {
    // Other
    disposition = randomItem(DISPOSITION_VALUES);
  }

  // Generate DisciplinaryAction based on disposition
  // If sustained, more likely to have disciplinary action
  let disciplinaryAction: string;
  if (isSustained(disposition)) {
    // 70% chance of disciplinary action if sustained
    if (Math.random() < 0.70) {
      // Weighted toward common actions
      const actionRand = Math.random();
      if (actionRand < 0.30) {
        disciplinaryAction = randomItem(DISCIPLINARY_ACTION_VALUES.filter(a =>
          a.toLowerCase().includes('counseling') || a.toLowerCase().includes('reprimand')
        ));
      } else if (actionRand < 0.50) {
        disciplinaryAction = randomItem(DISCIPLINARY_ACTION_VALUES.filter(a =>
          a.toLowerCase().includes('suspension')
        ));
      } else if (actionRand < 0.60) {
        disciplinaryAction = randomItem(DISCIPLINARY_ACTION_VALUES.filter(a =>
          a.toLowerCase().includes('training')
        ));
      } else {
        disciplinaryAction = randomItem(DISCIPLINARY_ACTION_VALUES);
      }
    } else {
      disciplinaryAction = randomItem(DISCIPLINARY_ACTION_VALUES.filter(a =>
        !hasDisciplinaryAction(a)
      ));
    }
  } else {
    // 20% chance of disciplinary action if not sustained
    if (Math.random() < 0.20) {
      disciplinaryAction = randomItem(DISCIPLINARY_ACTION_VALUES.filter(a =>
        a.toLowerCase().includes('counseling') || a.toLowerCase().includes('training')
      ));
    } else {
      disciplinaryAction = randomItem(DISCIPLINARY_ACTION_VALUES.filter(a =>
        !hasDisciplinaryAction(a)
      ));
    }
  }

  // Generate DateAssigned (60% chance, typically within 1-7 days of event date)
  let dateAssigned: string | null = null;
  if (Math.random() < 0.60) {
    const daysOffset = Math.floor(Math.random() * 7) + 1;
    dateAssigned = dayjs(eventDate).add(daysOffset, 'day').format('YYYY-MM-DD');
  }

  // Generate DateCompleted (50% chance if assigned, typically 30-180 days after assignment)
  let dateCompleted: string | null = null;
  if (dateAssigned && Math.random() < 0.50) {
    const daysOffset = Math.floor(Math.random() * 150) + 30;
    const completedDate = dayjs(dateAssigned).add(daysOffset, 'day');
    // Make sure completed date is not after end date
    if (completedDate.isBefore(dayjs(endDate)) || completedDate.isSame(dayjs(endDate))) {
      dateCompleted = completedDate.format('YYYY-MM-DD');
    }
  }

  return {
    OrgId: orgId,
    EventDate: eventDate,
    IncidentType: incidentType,
    CaseType: caseType,
    DisciplinaryAction: disciplinaryAction,
    ComplaintNature: complaintNature,
    Disposition: disposition,
    DateAssigned: dateAssigned,
    DateCompleted: dateCompleted,
  };
}

/**
 * Generate base Internal Affairs event data
 * 
 * Generates a collection of events with weighted randomization based on
 * patterns found in the sample data. The number of events is determined
 * by the date range - approximately 0.5-1.5 events per day on average
 * (fewer than vehicle pursuits).
 */
export function generateInternalAffairsBaseData(
  orgId: string,
  startDate: string,
  endDate: string
): InternalAffairsEvent[] {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const daysDiff = end.diff(start, 'day');

  // Generate approximately 0.5-1.5 events per day on average
  const avgEventsPerDay = 0.5 + Math.random(); // 0.5 to 1.5
  const numEvents = Math.floor(daysDiff * avgEventsPerDay);

  const events: InternalAffairsEvent[] = [];
  for (let i = 0; i < numEvents; i++) {
    events.push(generateEvent(orgId, startDate, endDate));
  }

  return events;
}

/**
 * Generator for Internal Affairs module data
 */
export class InternalAffairsGenerator {
  /**
   * Generate metrics for Internal Affairs module
   */
  static generateMetrics(
    orgId: string,
    startDate: string,
    endDate: string
  ): PerformanceReportingMetricsResponse {
    const baseData = generateInternalAffairsBaseData(orgId, startDate, endDate);

    // Total incidents is the count of events in the base data
    const totalIncidents = baseData.length;

    // Sustained incidents are events where Disposition contains "Sustained"
    const sustainedIncidents = baseData.filter((event) =>
      isSustained(event.Disposition)
    ).length;

    // Incidents with disciplinary action
    const disciplinaryActionIncidents = baseData.filter((event) =>
      hasDisciplinaryAction(event.DisciplinaryAction)
    ).length;

    const percentSustained =
      totalIncidents > 0
        ? Math.round((sustainedIncidents / totalIncidents) * 1000) / 1000
        : 0;

    const percentWithDisciplinaryAction =
      totalIncidents > 0
        ? Math.round((disciplinaryActionIncidents / totalIncidents) * 1000) / 1000
        : 0;

    const metrics: PerformanceReportingMetricsResponse = [
      {
        key: 'totalIncidents',
        label: 'Total Internal Affairs Incidents',
        value: totalIncidents,
        valueFormat: 'number',
        trend: Math.random() < 0.5 ? ('DOWN' as TrendValue) : ('UP' as TrendValue),
        description:
          'Total number of internal affairs incidents during the selected date range',
      },
      {
        key: 'percentSustained',
        label: 'Incidents Sustained',
        value: percentSustained,
        valueFormat: 'percent',
        trend: Math.random() < 0.5 ? ('DOWN' as TrendValue) : ('UP' as TrendValue),
        description: 'Percentage of incidents that were sustained',
      },
      {
        key: 'percentWithDisciplinaryAction',
        label: 'Incidents with Disciplinary Action',
        value: percentWithDisciplinaryAction,
        valueFormat: 'percent',
        trend: Math.random() < 0.5 ? ('DOWN' as TrendValue) : ('UP' as TrendValue),
        description: 'Percentage of incidents that resulted in disciplinary action',
      },
    ];

    return metrics;
  }

  /**
   * Calculate number of periods based on date range
   * If 31 days or less, use weeks; otherwise use months
   */
  private static calculatePeriods(
    startDate: string,
    endDate: string
  ): { useWeeks: boolean; periods: number; } {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const diffDays = end.diff(start, 'day');

    // If period is 31 days or less, use weeks
    const useWeeks = diffDays <= 31;

    if (useWeeks) {
      // Calculate number of weeks
      const weeks = Math.ceil(diffDays / 7);
      return { useWeeks: true, periods: Math.max(1, weeks) };
    }

    // For more than a month, calculate number of complete months
    const monthsDiff = end.diff(start, 'month') + 1; // +1 to include both start and end months

    return { useWeeks: false, periods: Math.max(1, monthsDiff) };
  }

  /**
   * Get the period start date for a given period index
   */
  private static getPeriodStartDate(
    startDate: string,
    periodIndex: number,
    useWeeks: boolean
  ): dayjs.Dayjs {
    const start = dayjs(startDate);
    if (useWeeks) {
      return start.add(periodIndex * 7, 'day');
    } else {
      return start.add(periodIndex, 'month').startOf('month');
    }
  }

  /**
   * Determine which period an event belongs to
   */
  private static getEventPeriod(
    eventDate: string,
    startDate: string,
    useWeeks: boolean
  ): number {
    const event = dayjs(eventDate);
    const start = dayjs(startDate);

    if (useWeeks) {
      const diffDays = event.diff(start, 'day');
      return Math.floor(diffDays / 7);
    } else {
      return event.diff(start, 'month');
    }
  }

  /**
   * Generate trends data for Internal Affairs module
   */
  static generateTrends(
    orgId: string,
    startDate: string,
    endDate: string
  ): PerformanceReportingTrendsResponse {
    const baseData = generateInternalAffairsBaseData(orgId, startDate, endDate);
    const { useWeeks, periods } = InternalAffairsGenerator.calculatePeriods(
      startDate,
      endDate
    );
    const periodMode = useWeeks ? 'week' : 'month';

    // Group events by period
    const periodData: Array<{
      total: number;
      sustained: number;
    }> = Array(periods).fill(null).map(() => ({ total: 0, sustained: 0 }));

    baseData.forEach((event) => {
      const periodIndex = InternalAffairsGenerator.getEventPeriod(
        event.EventDate,
        startDate,
        useWeeks
      );

      // Only count events within the valid period range
      if (periodIndex >= 0 && periodIndex < periods) {
        periodData[periodIndex].total++;
        if (isSustained(event.Disposition)) {
          periodData[periodIndex].sustained++;
        }
      }
    });

    // Build trend series data
    const totalData = periodData.map((data, index) => {
      const periodDate = InternalAffairsGenerator.getPeriodStartDate(
        startDate,
        index,
        useWeeks
      );
      const period = periodDate.format('YYYY-MM-DD');

      return {
        period,
        value: data.total,
        description: [
          `${data.total} incident${data.total === 1 ? '' : 's'}`,
          'Total incidents',
        ],
      };
    });

    const sustainedData = periodData.map((data, index) => {
      const periodDate = InternalAffairsGenerator.getPeriodStartDate(
        startDate,
        index,
        useWeeks
      );
      const period = periodDate.format('YYYY-MM-DD');

      return {
        period,
        value: data.sustained,
        description: [
          `${data.sustained} incident${data.sustained === 1 ? '' : 's'}`,
          'Sustained incidents',
        ],
      };
    });

    return {
      periodMode,
      series: [
        {
          name: 'total',
          data: totalData,
        },
        {
          name: 'sustained',
          data: sustainedData,
        },
      ],
    };
  }

  /**
   * Generate dimensions data for Internal Affairs module
   */
  static generateDimensions(
    orgId: string,
    startDate: string,
    endDate: string
  ): PerformanceReportingDimensionsResponse {
    const baseData = generateInternalAffairsBaseData(orgId, startDate, endDate);
    const dimensions: PerformanceReportingDimensionsResponse = [];
    const subject = 'incident';

    // Dimension 1: Disciplinary Action
    const disciplinaryActionCounts: Record<string, number> = {};
    baseData.forEach((event) => {
      disciplinaryActionCounts[event.DisciplinaryAction] =
        (disciplinaryActionCounts[event.DisciplinaryAction] || 0) + 1;
    });

    const totalDisciplinaryActions = baseData.length;
    const disciplinaryActionData = Object.entries(disciplinaryActionCounts)
      .map(([name, value]) => ({
        name,
        value,
        description: formatTooltipDescription(value, totalDisciplinaryActions, subject),
      }))
      .sort((a, b) => b.value - a.value); // Sort descending

    dimensions.push({
      name: 'Disciplinary Action',
      data: disciplinaryActionData,
    });

    // Dimension 2: Disposition
    const dispositionCounts: Record<string, number> = {};
    baseData.forEach((event) => {
      dispositionCounts[event.Disposition] =
        (dispositionCounts[event.Disposition] || 0) + 1;
    });

    const totalDispositions = baseData.length;
    const dispositionData = Object.entries(dispositionCounts)
      .map(([name, value]) => ({
        name,
        value,
        description: formatTooltipDescription(value, totalDispositions, subject),
      }))
      .sort((a, b) => b.value - a.value); // Sort descending

    dimensions.push({
      name: 'Disposition',
      data: dispositionData,
    });

    // Dimension 3: Complaint Nature
    const complaintNatureCounts: Record<string, number> = {};
    baseData.forEach((event) => {
      complaintNatureCounts[event.ComplaintNature] =
        (complaintNatureCounts[event.ComplaintNature] || 0) + 1;
    });

    const totalComplaintNatures = baseData.length;
    const complaintNatureData = Object.entries(complaintNatureCounts)
      .map(([name, value]) => ({
        name,
        value,
        description: formatTooltipDescription(value, totalComplaintNatures, subject),
      }))
      .sort((a, b) => b.value - a.value); // Sort descending

    dimensions.push({
      name: 'Complaint Nature',
      data: complaintNatureData,
    });

    return dimensions;
  }

  /**
   * Generate top-5 summaries for Internal Affairs module
   */
  static generateTop5Summaries(
    orgId: string,
    startDate: string,
    endDate: string
  ): PerformanceReportingTop5SummariesResponse {
    const baseData = generateInternalAffairsBaseData(orgId, startDate, endDate);
    const subject = 'incident';
    const summaries: PerformanceReportingTop5SummariesResponse = [];

    // Summary 1: Disposition (top 5)
    const dispositionCounts: Record<string, number> = {};
    baseData.forEach((event) => {
      dispositionCounts[event.Disposition] =
        (dispositionCounts[event.Disposition] || 0) + 1;
    });

    const totalDispositions = Object.values(dispositionCounts).reduce(
      (sum, count) => sum + count,
      0
    );

    const top5Dispositions = Object.entries(dispositionCounts)
      .map(([name, value]) => ({
        name,
        value,
        description: formatTooltipDescription(value, totalDispositions, subject),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    summaries.push({
      name: 'Disposition',
      data: top5Dispositions,
    });

    // Summary 2: Disciplinary Action (top 5)
    const disciplinaryActionCounts: Record<string, number> = {};
    baseData.forEach((event) => {
      disciplinaryActionCounts[event.DisciplinaryAction] =
        (disciplinaryActionCounts[event.DisciplinaryAction] || 0) + 1;
    });

    const totalDisciplinaryActions = Object.values(disciplinaryActionCounts).reduce(
      (sum, count) => sum + count,
      0
    );

    const top5DisciplinaryActions = Object.entries(disciplinaryActionCounts)
      .map(([name, value]) => ({
        name,
        value,
        description: formatTooltipDescription(value, totalDisciplinaryActions, subject),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    summaries.push({
      name: 'Disciplinary Action',
      data: top5DisciplinaryActions,
    });

    return summaries;
  }
}
